import React, { useState, useEffect, useRef } from 'react';
import { Icon, Button, Dropdown, Input } from 'semantic-ui-react';
import Highlighter from 'react-highlight-words';
import moment from 'moment';
import Loader from '../Loader';
import generateAISuggestions from '../../services/generateAISuggestions';
import fetchCronJobHistory from '../../services/fetchCronJobHistory';
import fetchCronJobLogs from '../../services/fetchCronJobLogs';
import downloadCronJobLog from '../../services/downloadCronJobLog';
import './ErrorConsoleAI.scss';

const ErrorConsoleAI = ({ isOpen, onClose, jobData, selectedYear, selectedMonth, adminId }) => {
    const [suggestions, setSuggestions] = useState(null);
    const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
    const [error, setError] = useState(null);
    const [history, setHistory] = useState([]);
    const [logs, setLogs] = useState([]);
    const [isLoadingHistory, setIsLoadingHistory] = useState(false);
    const [isLoadingLogs, setIsLoadingLogs] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);
    const [downloadedFile, setDownloadedFile] = useState(null);
    const [selectedDay, setSelectedDay] = useState(null);
    const [viewMode, setViewMode] = useState('overview'); // 'overview', 'history', 'logs'
    const [logSearchTerm, setLogSearchTerm] = useState('');
    const [logMatchIndex, setLogMatchIndex] = useState(0);
    const [logMatchTotal, setLogMatchTotal] = useState(0);
    const consoleRef = useRef(null);
    const logTextRef = useRef(null);

    useEffect(() => {
        if (isOpen && consoleRef.current) {
            consoleRef.current.focus();
        }

        const handleEscape = (e) => {
            if (e.key === 'Escape' && isOpen) {
                onClose();
            }
        };

        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [isOpen, onClose]);

    // Recompute total matches and reset index when search term or logs change
    useEffect(() => {
        if (!logSearchTerm) {
            setLogMatchTotal(0);
            setLogMatchIndex(0);
            return;
        }
        const content = logs.map(l => l.content || l.message || '').join('\n');
        const stripped = stripAnsiCodes(content);
        const total = stripped.toLowerCase().split(logSearchTerm.toLowerCase()).length - 1;
        setLogMatchTotal(total);
        setLogMatchIndex(0);
    }, [logSearchTerm, logs]);

    // Scroll to active match and toggle active highlight class
    useEffect(() => {
        if (!logTextRef.current || !logSearchTerm) return;
        const highlights = logTextRef.current.querySelectorAll('.log-highlight');
        highlights.forEach((el, i) => {
            el.classList.toggle('log-highlight-active', i === logMatchIndex);
        });
        if (highlights[logMatchIndex]) {
            highlights[logMatchIndex].scrollIntoView({ block: 'center', behavior: 'smooth' });
        }
    }, [logMatchIndex, logSearchTerm, logs]);

    // jobData.jobName added so switching jobs always reloads
    useEffect(() => {
        if (isOpen && viewMode === 'history') {
            setError(null);
            loadHistory();
        }
    }, [isOpen, viewMode, selectedYear, selectedMonth, selectedDay, jobData && jobData.jobName]);

    useEffect(() => {
        if (isOpen && viewMode === 'logs') {
            setError(null);
            loadLogs();
        }
    }, [isOpen, viewMode, selectedYear, selectedMonth, selectedDay, jobData && jobData.jobName]);

    const handleGenerateSuggestions = async (errorOverride) => {
        try {
            setIsLoadingSuggestions(true);
            setError(null);
            setViewMode('overview');

            const payload = {
                jobName: jobData.jobName,
                lastError: errorOverride || (jobData.state && jobData.state.lastFailureError) || 'Unknown error',
                lastFailureAt: jobData.state && jobData.state.lastFailureAt,
                lastSuccessAt: jobData.state && jobData.state.lastSuccessAt,
                consecutiveFailures: (jobData.metrics && jobData.metrics.consecutiveFailures) || 0,
                totalRuns: (jobData.metrics && jobData.metrics.totalRuns) || 0,
                successRate: (jobData.metrics && jobData.metrics.successRate) || 0
            };

            const response = await generateAISuggestions(payload, adminId);
            
            if (response.data && response.data.data && response.data.data.suggestions) {
                setSuggestions(response.data.data.suggestions);
            } else if (response.data && response.data.suggestions) {
                setSuggestions(response.data.suggestions);
            } else {
                setError('No suggestions received from AI');
            }
        } catch (err) {
            console.error('Error generating AI suggestions:', err);
            setError((err.response && err.response.data && err.response.data.message) || 'Failed to generate suggestions. Please try again.');
        } finally {
            setIsLoadingSuggestions(false);
        }
    };

    const formatDate = (date) => {
        if (!date) return 'Never';
        return moment(date).format('DD MMM YYYY, HH:mm:ss');
    };

    const formatRelativeTime = (date) => {
        if (!date) return '';
        return moment(date).fromNow();
    };

    const formatDuration = (ms) => {
        if (!ms) return 'N/A';
        if (ms < 1000) return `${ms}ms`;
        if (ms < 60000) return `${(ms / 1000).toFixed(2)}s`;
        return `${(ms / 60000).toFixed(2)}m`;
    };

    const stripAnsiCodes = (str) => {
        if (!str) return '';
        // Remove ANSI escape codes (color codes like [32m, [39m, etc.)
        return str.replace(/\x1b\[[0-9;]*m/g, '').replace(/\[([0-9]{1,2}m)/g, '');
    };

    const parseAISuggestions = (text) => {
        if (!text) return null;

        // Split by lines and process
        const lines = text.split('\n');
        const elements = [];
        let currentList = [];

        lines.forEach((line, index) => {
            const trimmed = line.trim();

            // Handle empty lines
            if (!trimmed) {
                if (currentList.length > 0) {
                    elements.push(
                        <ul key={`list-${index}`} className="ai-list">
                            {currentList.map((item, idx) => (
                                <li key={idx}>{parseLineContent(item)}</li>
                            ))}
                        </ul>
                    );
                    currentList = [];
                }
                elements.push(<div key={`empty-${index}`} className="ai-spacer" />);
                return;
            }

            // Handle list items (lines starting with * or -)
            if (trimmed.startsWith('*') || trimmed.startsWith('-')) {
                const content = trimmed.replace(/^[\*\-]\s+/, '');
                currentList.push(content);
            } else {
                // Flush any pending list
                if (currentList.length > 0) {
                    elements.push(
                        <ul key={`list-${index}`} className="ai-list">
                            {currentList.map((item, idx) => (
                                <li key={idx}>{parseLineContent(item)}</li>
                            ))}
                        </ul>
                    );
                    currentList = [];
                }

                // Handle headings (lines starting with #)
                if (trimmed.startsWith('##')) {
                    const heading = trimmed.replace(/^#+\s+/, '');
                    elements.push(
                        <h3 key={`h3-${index}`} className="ai-heading-3">
                            {parseLineContent(heading)}
                        </h3>
                    );
                } else if (trimmed.startsWith('#')) {
                    const heading = trimmed.replace(/^#+\s+/, '');
                    elements.push(
                        <h2 key={`h2-${index}`} className="ai-heading-2">
                            {parseLineContent(heading)}
                        </h2>
                    );
                } else {
                    // Regular paragraph
                    elements.push(
                        <p key={`p-${index}`} className="ai-paragraph">
                            {parseLineContent(trimmed)}
                        </p>
                    );
                }
            }
        });

        // Flush any remaining list
        if (currentList.length > 0) {
            elements.push(
                <ul key="final-list" className="ai-list">
                    {currentList.map((item, idx) => (
                        <li key={idx}>{parseLineContent(item)}</li>
                    ))}
                </ul>
            );
        }

        return elements;
    };

    const parseLineContent = (text) => {
        if (!text) return null;

        const parts = [];
        let lastIndex = 0;

        // Regex to match **bold**, *italic*, `code`, and [links]
        const regex = /\*\*(.+?)\*\*|\*(.+?)\*|`(.+?)`|\[(.+?)\]\((.+?)\)/g;
        let match;

        while ((match = regex.exec(text)) !== null) {
            // Add text before match
            if (match.index > lastIndex) {
                parts.push(text.substring(lastIndex, match.index));
            }

            // Add formatted match
            if (match[1]) {
                // **bold**
                parts.push(
                    <strong key={`bold-${match.index}`} className="ai-bold">
                        {match[1]}
                    </strong>
                );
            } else if (match[2]) {
                // *italic*
                parts.push(
                    <em key={`italic-${match.index}`} className="ai-italic">
                        {match[2]}
                    </em>
                );
            } else if (match[3]) {
                // `code`
                parts.push(
                    <code key={`code-${match.index}`} className="ai-code">
                        {match[3]}
                    </code>
                );
            } else if (match[4] && match[5]) {
                // [link](url)
                parts.push(
                    <a
                        key={`link-${match.index}`}
                        href={match[5]}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="ai-link"
                    >
                        {match[4]}
                    </a>
                );
            }

            lastIndex = regex.lastIndex;
        }

        // Add remaining text
        if (lastIndex < text.length) {
            parts.push(text.substring(lastIndex));
        }

        return parts.length > 0 ? parts : text;
    };

    const loadHistory = async () => {
        try {
            setIsLoadingHistory(true);
            const params = {
                year: selectedYear,
                month: selectedMonth,
                day: selectedDay || moment().date()
            };
            const response = await fetchCronJobHistory(jobData.adminId, jobData.jobName, params);
            if (response.data && response.data.data) {
                setHistory(response.data.data.history || []);
            }
        } catch (err) {
            console.error('Error loading history:', err);
            setError('Failed to load history data');
        } finally {
            setIsLoadingHistory(false);
        }
    };

    const handleDownloadLogs = async () => {
        try {
            setIsDownloading(true);
            setDownloadedFile(null);
            const day = selectedDay || moment().date();
            const params = { year: selectedYear, month: selectedMonth, day };
            const response = await downloadCronJobLog(jobData.adminId, jobData.jobName, params);
            const filename = `${jobData.jobName}-${selectedYear}-${selectedMonth}-${day}.log`;
            const blob = new Blob([response.data], { type: 'text/plain' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
            setDownloadedFile(filename);
        } catch (err) {
            console.error('Error downloading logs:', err);
        } finally {
            setIsDownloading(false);
        }
    };

    const handleCopyTerminalCmd = (cmd) => {
        navigator.clipboard.writeText(cmd).catch(() => {
            const ta = document.createElement('textarea');
            ta.value = cmd;
            document.body.appendChild(ta);
            ta.select();
            document.execCommand('copy');
            document.body.removeChild(ta);
        });
    };

    const loadLogs = async () => {
        try {
            setIsLoadingLogs(true);
            const params = {
                year: selectedYear,
                month: selectedMonth
            };
            if (selectedDay) {
                params.day = selectedDay;
            }
            const response = await fetchCronJobLogs(jobData.adminId, jobData.jobName, params);
            if (response.data && response.data.data) {
                const logsData = response.data.data.logs;
                
                // If logs is a string, store it as a single item
                if (typeof logsData === 'string') {
                    setLogs(logsData ? [{ content: logsData }] : []);
                } else if (Array.isArray(logsData)) {
                    setLogs(logsData);
                } else {
                    setLogs([]);
                }
            }
        } catch (err) {
            console.error('Error loading logs:', err);
            setError('Failed to load logs data');
        } finally {
            setIsLoadingLogs(false);
        }
    };

    // Generate day options for the selected month
    const getDayOptions = () => {
        const daysInMonth = moment(`${selectedYear}-${selectedMonth}`, 'YYYY-M').daysInMonth();
        const options = [{ key: 'all', text: 'All Days', value: null }];
        for (let i = 1; i <= daysInMonth; i++) {
            options.push({
                key: i,
                text: i.toString(),
                value: i
            });
        }
        return options;
    };

    if (!isOpen) return null;

    return (
        <>
            <div className="error-console-overlay" onClick={onClose} />
            <div 
                className={`error-console-ai ${isOpen ? 'open' : ''}`}
                ref={consoleRef}
                tabIndex={-1}
            >
                <div className="console-header">
                    <div className="header-content">
                        <Icon
                            loading={jobData.state && jobData.state.currentStatus === 'running'}
                            name={
                                jobData.state && jobData.state.currentStatus === 'success' ? 'info circle' :
                                jobData.state && jobData.state.currentStatus === 'running' ? 'spinner' :
                                'bug'
                            }
                            size="large"
                        />
                        <h2>
                            {jobData.state && jobData.state.currentStatus === 'success' ? 'Job Details' :
                             jobData.state && jobData.state.currentStatus === 'running' ? 'Job Running' :
                             'Error Console AI'}
                        </h2>
                    </div>
                    <Icon 
                        name="close" 
                        size="large" 
                        link 
                        onClick={onClose}
                        className="close-icon"
                    />
                </div>

                <div className="console-body">
                    {/* View Mode Tabs */}
                    <div className="view-mode-tabs">
                        <Button.Group fluid>
                            <Button
                                active={viewMode === 'overview'}
                                onClick={() => setViewMode('overview')}
                            >
                                <Icon name="info circle" />
                                Overview
                            </Button>
                            <Button
                                active={viewMode === 'history'}
                                onClick={() => setViewMode('history')}
                            >
                                <Icon name="history" />
                                History
                            </Button>
                            <Button
                                active={viewMode === 'logs'}
                                onClick={() => setViewMode('logs')}
                            >
                                <Icon name="file text" />
                                Logs
                            </Button>
                        </Button.Group>
                    </div>

                    {/* Day Selector + Reload (for history and logs) */}
                    {(viewMode === 'history' || viewMode === 'logs') && (
                        <div className="day-selector">
                            <span className="selector-label">Day:</span>
                            <Dropdown
                                selection
                                options={getDayOptions()}
                                value={selectedDay}
                                onChange={(e, { value }) => setSelectedDay(value)}
                                placeholder="Select day"
                            />
                            <Button
                                size="small"
                                className="reload-btn"
                                onClick={() => {
                                    setError(null);
                                    if (viewMode === 'history') loadHistory();
                                    else loadLogs();
                                }}
                                title="Reload"
                            >
                                <Icon name="refresh" />
                                Reload
                            </Button>
                        </div>
                    )}

                    {/* Overview Mode */}
                    {viewMode === 'overview' && (
                        <>
                            {/* Job Information Section */}
                            <div className="info-section">
                        <div className="section-title">
                            <Icon name="briefcase" />
                            Job Information
                        </div>
                        <div className="info-card">
                            <div className="info-item">
                                <span className="label">Job Name:</span>
                                <span className="value">{jobData.jobName}</span>
                            </div>
                            <div className="info-item">
                                <span className="label">Current Status:</span>
                                <span className={`value status-badge ${
                                    (jobData.state && jobData.state.currentStatus) === 'success' ? 'success' :
                                    (jobData.state && jobData.state.currentStatus) === 'running' ? 'running' :
                                    'failure'
                                }`}>
                                    <Icon
                                        loading={(jobData.state && jobData.state.currentStatus) === 'running'}
                                        name={
                                            (jobData.state && jobData.state.currentStatus) === 'success' ? 'check circle' :
                                            (jobData.state && jobData.state.currentStatus) === 'running' ? 'spinner' :
                                            'times circle'
                                        }
                                    />
                                    {(jobData.state && jobData.state.currentStatus) || 'Unknown'}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Execution Timeline Section */}
                    <div className="info-section">
                        <div className="section-title">
                            <Icon name="clock outline" />
                            Execution Timeline
                        </div>
                        <div className="timeline-grid">
                            <div className="timeline-item success">
                                <div className="timeline-icon">
                                    <Icon name="check circle" />
                                </div>
                                <div className="timeline-content">
                                    <div className="timeline-label">Last Success</div>
                                    <div className="timeline-date">
                                        {formatDate(jobData.state && jobData.state.lastSuccessAt)}
                                    </div>
                                    {jobData.state && jobData.state.lastSuccessAt && (
                                        <div className="timeline-relative">
                                            {formatRelativeTime(jobData.state.lastSuccessAt)}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="timeline-item failure">
                                <div className="timeline-icon">
                                    <Icon name="times circle" />
                                </div>
                                <div className="timeline-content">
                                    <div className="timeline-label">Last Failure</div>
                                    <div className="timeline-date">
                                        {formatDate(jobData.state && jobData.state.lastFailureAt)}
                                    </div>
                                    {jobData.state && jobData.state.lastFailureAt && (
                                        <div className="timeline-relative">
                                            {formatRelativeTime(jobData.state.lastFailureAt)}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Error Details Section */}
                    <div className="info-section">
                        <div className="section-title">
                            <Icon name="exclamation triangle" />
                            Error Details
                        </div>
                        <div className="error-details-card">
                            <div className="error-message">
                                {(jobData.state && jobData.state.lastFailureError) || 'No error message available'}
                            </div>
                            <div className="error-stats">
                                <div className="stat-item">
                                    <Icon name="redo" />
                                    <span className="stat-label">Total Runs:</span>
                                    <span className="stat-value">{(jobData.metrics && jobData.metrics.totalRuns) || 0}</span>
                                </div>
                                <div className="stat-item">
                                    <Icon name="warning sign" />
                                    <span className="stat-label">Consecutive Failures:</span>
                                    <span className="stat-value failure-count">
                                        {(jobData.metrics && jobData.metrics.consecutiveFailures) || 0}
                                    </span>
                                </div>
                                <div className="stat-item">
                                    <Icon name="chart line" />
                                    <span className="stat-label">Success Rate:</span>
                                    <span className="stat-value">
                                        {(jobData.metrics && jobData.metrics.successRate) ? `${jobData.metrics.successRate}%` : 'N/A'}
                                    </span>
                                </div>
                                <div className="stat-item">
                                    <Icon name="chart line" />
                                    <span className="stat-label">Total Success last 24 Hrs:</span>
                                    <span className="stat-value">
                                        {(jobData.metrics && jobData.metrics.last24Hours.successes) ? jobData.metrics.last24Hours.successes : 'N/A'}
                                    </span>
                                </div>
                                <div className="stat-item">
                                    <Icon name="chart line" />
                                    <span className="stat-label">Total Success last 7 Days:</span>
                                    <span className="stat-value">
                                        {(jobData.metrics && jobData.metrics.last7Days.successes) ? jobData.metrics.last7Days.successes : 'N/A'}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* AI Suggestions Section */}
                    {suggestions && (
                        <div className="info-section suggestions-section">
                            <div className="section-title">
                                <Icon name="lightbulb outline" />
                                AI Suggestions
                            </div>
                            <div className="suggestions-card">
                                <div className="suggestions-content">
                                    {parseAISuggestions(suggestions)}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Error State */}
                    {error && viewMode === 'overview' && (
                        <div className="error-state">
                            <Icon name="warning circle" size="large" />
                            <p>{error}</p>
                        </div>
                    )}
                        </>
                    )}

                    {/* History Mode */}
                    {viewMode === 'history' && (
                        <div className="data-view-section">
                            {isLoadingHistory ? (
                                <div className="loading-state">
                                    <Loader />
                                </div>
                            ) : error ? (
                                <div className="error-state">
                                    <Icon name="warning circle" size="large" />
                                    <p>{error}</p>
                                </div>
                            ) : history.length > 0 ? (
                                <div className="history-list">
                                    <div className="history-summary">
                                        <span className="summary-badge success">
                                            <Icon name="check circle" />
                                            {history.filter(h => h.status === 'success').length} Successful
                                        </span>
                                        <span className="summary-badge failure">
                                            <Icon name="times circle" />
                                            {history.filter(h => h.status !== 'success').length} Failed
                                        </span>
                                    </div>
                                    {history.map((item, index) => (
                                        <div key={index} className={`history-item ${item.status === 'success' ? '' : 'failure'}`}>
                                            <div className="history-header">
                                                <span className={`status-badge ${item.status === 'success' ? 'success' : 'failure'}`}>
                                                    <Icon name={item.status === 'success' ? 'check circle' : 'times circle'} />
                                                    {item.status}
                                                </span>
                                                <span className="history-time">
                                                    {formatDate(item.timestamp)}
                                                </span>
                                                {item.status !== 'success' && (
                                                    <Button
                                                        size="mini"
                                                        className="history-debug-btn"
                                                        onClick={() => handleGenerateSuggestions(item.error)}
                                                        disabled={isLoadingSuggestions}
                                                        title="Debug this failure with AI"
                                                    >
                                                        <Icon name="magic" />
                                                        Debug with AI
                                                    </Button>
                                                )}
                                            </div>
                                            {item.duration && (
                                                <div className="history-duration">
                                                    Duration: {formatDuration(item.duration)}
                                                </div>
                                            )}
                                            {item.error && (
                                                <div className="history-error">
                                                    <strong>Error:</strong> {item.error}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="empty-state">
                                    <Icon name="inbox" size="huge" />
                                    <p>No history data available for the selected period</p>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Logs Mode */}
                    {viewMode === 'logs' && (
                        <div className="data-view-section">
                            <div className="logs-toolbar">
                                <Input
                                    size="small"
                                    icon="search"
                                    placeholder="Find in logs..."
                                    value={logSearchTerm}
                                    onChange={(e) => setLogSearchTerm(e.target.value)}
                                    className="log-search-input"
                                />
                                {logSearchTerm && logMatchTotal > 0 && (
                                    <>
                                        <span className="log-match-count">
                                            {logMatchIndex + 1}/{logMatchTotal}
                                        </span>
                                        <Button
                                            size="small"
                                            className="match-nav-btn"
                                            icon
                                            title="Previous match"
                                            onClick={() => setLogMatchIndex(i => (i - 1 + logMatchTotal) % logMatchTotal)}
                                        >
                                            <Icon name="chevron up" />
                                        </Button>
                                        <Button
                                            size="small"
                                            className="match-nav-btn"
                                            icon
                                            title="Next match"
                                            onClick={() => setLogMatchIndex(i => (i + 1) % logMatchTotal)}
                                        >
                                            <Icon name="chevron down" />
                                        </Button>
                                    </>
                                )}
                                {logSearchTerm && logMatchTotal === 0 && (
                                    <span className="log-match-count no-matches">No matches</span>
                                )}
                                <Button
                                    size="small"
                                    className="scroll-latest-btn"
                                    onClick={() => {
                                        if (logTextRef.current) {
                                            logTextRef.current.scrollTop = logTextRef.current.scrollHeight;
                                        }
                                    }}
                                    title="Jump to latest log"
                                >
                                    <Icon name="arrow down" />
                                    Latest
                                </Button>
                                <Button
                                    size="small"
                                    onClick={handleDownloadLogs}
                                    disabled={isDownloading}
                                    className="download-log-btn"
                                >
                                    <Icon loading={isDownloading} name={isDownloading ? 'spinner' : 'cloud download'} />
                                    {isDownloading ? 'Downloading...' : 'Download Log'}
                                </Button>
                            </div>
                            {downloadedFile && (
                                <div className="terminal-tip">
                                    <div className="terminal-tip-header">
                                        <Icon name="terminal" />
                                        <span>Open in terminal:</span>
                                    </div>
                                    <div className="terminal-tip-row">
                                        <code className="terminal-cmd">less ~/Downloads/{downloadedFile}</code>
                                        <button
                                            className="copy-cmd-btn"
                                            onClick={() => handleCopyTerminalCmd(`less ~/Downloads/${downloadedFile}`)}
                                            title="Copy command"
                                        >
                                            <Icon name="copy outline" />
                                        </button>
                                    </div>
                                </div>
                            )}
                            {isLoadingLogs ? (
                                <div className="loading-state">
                                    <Loader />
                                </div>
                            ) : error ? (
                                <div className="error-state">
                                    <Icon name="warning circle" size="large" />
                                    <p>{error}</p>
                                </div>
                            ) : logs.length > 0 ? (
                                <div className="logs-container">
                                    {logs.map((log, index) => (
                                        <div key={index} className="log-content">
                                            {log.content ? (
                                                <pre className="log-text" ref={logTextRef}>
                                                    <Highlighter
                                                        highlightClassName="log-highlight"
                                                        searchWords={logSearchTerm ? [logSearchTerm] : []}
                                                        autoEscape
                                                        textToHighlight={stripAnsiCodes(log.content)}
                                                    />
                                                </pre>
                                            ) : (
                                                <div className="log-item">
                                                    {log.timestamp && (
                                                        <div className="log-timestamp">
                                                            {formatDate(log.timestamp)}
                                                        </div>
                                                    )}
                                                    <div className="log-message">
                                                        {log.message || log}
                                                    </div>
                                                    {log.level && (
                                                        <span className={`log-level ${log.level.toLowerCase()}`}>
                                                            {log.level}
                                                        </span>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="empty-state">
                                    <Icon name="inbox" size="huge" />
                                    <p>No logs available for the selected period</p>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer with AI Button - Only show for failed jobs (not running) */}
                {jobData.state && jobData.state.currentStatus !== 'success' && jobData.state.currentStatus !== 'running' && (
                    <div className="console-footer">
                        <Button
                            primary
                            fluid
                            size="large"
                            onClick={handleGenerateSuggestions}
                            disabled={isLoadingSuggestions}
                            className="generate-suggestions-btn"
                        >
                            {isLoadingSuggestions ? (
                                <>
                                    <Icon loading name="spinner" />
                                    Generating AI Suggestions...
                                </>
                            ) : (
                                <>
                                    <Icon name="magic" />
                                    Generate AI Suggestions
                                </>
                            )}
                        </Button>
                    </div>
                )}
            </div>
        </>
    );
};

export default ErrorConsoleAI;
